import { doc, collection, onSnapshot, query, orderBy, Unsubscribe } from 'firebase/firestore';
import db from '@/firebase/init';

/**
 * Agentic Task Log status constants
 */
export const AGENTIC_TASK_LOG_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  ERROR: 'error'
} as const;

/**
 * Log entry type constants
 */
export const ENTRY_TYPE = {
  STEP_STARTED: 'step_started',
  STEP_COMPLETED: 'step_completed',
  LOG: 'log',
  THINKING: 'thinking',
  OUTPUT: 'output',
  ERROR: 'error'
} as const;

/**
 * Interface for the current step state
 */
export interface CurrentStep {
  number: number;
  name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'error';
}

/**
 * Interface for Firestore agentic task log document
 */
export interface AgenticTaskLogDocument {
  taskType: number;
  status: 'pending' | 'processing' | 'completed' | 'error';
  totalSteps: number;
  currentStep?: CurrentStep;
  input?: Record<string, any>;
  output?: Record<string, any>;
  error?: string;
  userId: string;
  createdAt: any;
  updatedAt: any;
  completedAt?: any;
}

/**
 * Interface for a log entry
 */
export interface LogEntry {
  type: 'step_started' | 'step_completed' | 'log' | 'thinking' | 'output' | 'error';
  stepNumber: number;
  stepName?: string;
  content?: string | Record<string, any>;
  metadata?: Record<string, any>;
  timestamp: any;
}

/**
 * Callbacks for agentic task log subscription
 */
export interface AgenticTaskLogCallbacks {
  onProgress: (step: number, totalSteps: number, stepName: string) => void;
  onThinking: (thinking: string) => void;
  onResult: (result: any) => void;
  onError: (error: string) => void;
  onLogEntry?: (entry: LogEntry) => void;
  onClose?: () => void;
}

/**
 * Subscribe to an agentic task log in Firestore for real-time updates
 *
 * This subscribes to both:
 * 1. The main document for status and current step updates
 * 2. The entries sub-collection for log entries (thinking, etc.)
 *
 * @param agencyId - The agency ID (tenant ID)
 * @param taskId - The agentic task ID (same as Postgres AgenticTask.ID)
 * @param callbacks - Callbacks for progress, thinking, result, and error events
 * @returns Unsubscribe function to stop listening to both subscriptions
 */
export function subscribeToAgenticTaskLog(
  agencyId: string,
  taskId: string,
  callbacks: AgenticTaskLogCallbacks
): Unsubscribe {
  const docRef = doc(db, `tenants/${agencyId}/agenticTaskLogs/${taskId}`);
  const entriesRef = collection(db, `tenants/${agencyId}/agenticTaskLogs/${taskId}/entries`);
  const entriesQuery = query(entriesRef, orderBy('timestamp', 'asc'));

  let hasCompleted = false;
  let lastStepNumber = 0;
  const unsubscribers: Unsubscribe[] = [];

  console.log('[AgenticTaskLog] Subscribing to:', `tenants/${agencyId}/agenticTaskLogs/${taskId}`);

  // Subscribe to main document for status updates
  const docUnsubscribe = onSnapshot(docRef, (snapshot) => {
    if (!snapshot.exists()) {
      console.log('[AgenticTaskLog] Document does not exist yet, waiting...');
      return;
    }

    const data = snapshot.data() as AgenticTaskLogDocument;
    console.log('[AgenticTaskLog] Main doc update:', {
      status: data.status,
      currentStep: data.currentStep?.number,
      stepName: data.currentStep?.name
    });

    // Progress updates from current step
    if (data.currentStep && data.currentStep.number !== lastStepNumber) {
      lastStepNumber = data.currentStep.number;
      callbacks.onProgress(data.currentStep.number, data.totalSteps, data.currentStep.name);
    }

    // Completion states - only fire once
    if (!hasCompleted) {
      if (data.status === AGENTIC_TASK_LOG_STATUS.COMPLETED && data.output) {
        hasCompleted = true;
        console.log('[AgenticTaskLog] Task completed with result');
        callbacks.onResult(data.output);
        callbacks.onClose?.();
      } else if (data.status === AGENTIC_TASK_LOG_STATUS.ERROR) {
        hasCompleted = true;
        console.log('[AgenticTaskLog] Task failed with error:', data.error);
        callbacks.onError(data.error || 'Unknown error');
        callbacks.onClose?.();
      }
    }
  }, (error) => {
    console.error('[AgenticTaskLog] Main doc snapshot error:', error);
    if (!hasCompleted) {
      hasCompleted = true;
      callbacks.onError('Connection error: ' + error.message);
      callbacks.onClose?.();
    }
  });
  unsubscribers.push(docUnsubscribe);

  // Subscribe to entries sub-collection for log entries
  const entriesUnsubscribe = onSnapshot(entriesQuery, (snapshot) => {
    snapshot.docChanges().forEach((change) => {
      if (change.type === 'added') {
        const entry = change.doc.data() as LogEntry;

        // Call onLogEntry for all entry types if provided
        callbacks.onLogEntry?.(entry);

        // Handle thinking entries specifically
        if (entry.type === ENTRY_TYPE.THINKING && entry.content) {
          callbacks.onThinking(entry.content as string);
        }
      }
    });
  }, (error) => {
    console.error('[AgenticTaskLog] Entries snapshot error:', error);
  });
  unsubscribers.push(entriesUnsubscribe);

  // Return combined unsubscribe function
  return () => {
    console.log('[AgenticTaskLog] Unsubscribing from task:', taskId);
    unsubscribers.forEach(unsub => unsub());
  };
}

export default {
  subscribeToAgenticTaskLog,
  AGENTIC_TASK_LOG_STATUS,
  ENTRY_TYPE
};
